import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import { notBlank } from "../../../util/ext";
import SelectedFilesCheckbox from "../files/SelectedFilesCheckbox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleMinus,
  faFolderPlus,
  faCopy,
} from "@fortawesome/pro-regular-svg-icons";
import {
  IncludeDialogTypes,
  isContentGroupTopLevelView,
  isContentGroupsHome,
  isUngroupedHome,
  useView,
} from "../organizeUtil";
import { OrganizeContext } from "../organize-context";
import { useMediaTable } from "../../common/use-mediaTable";

import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import ContentGroupFilesTable from "./ContentGroupFilesTable";
import MediaBrowser from "../../common/MediaBrowser";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import CustomTablePagination from "../../common/CustomTablePagination";

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "white",
    // for loading indicator
    position: "relative",
  },

  header: {
    padding: theme.spacing(5),
  },

  loadingIndicator: {
    paddingTop: theme.spacing(5),
  },

  moduleContainer: {
    padding: theme.spacing(3),
  },

  moduleDetails: {
    paddingLeft: theme.spacing(3),
  },

  search: {
    marginTop: theme.spacing(2),
  },

  thumbnailEmpty: {
    fontSize: "75px",
    color: theme.palette.primary.disabled,
  },

  thumbnailOverlay: {
    height: "100px",
    width: "100px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    backgroundColor: "#E5E5E5",
  },
}));

const ContentGroupFilesBrowser = props => {
  const classes = useStyles();
  const view = useView();

  const {
    parent,
    content,
    loading,
    page,
    total,
    orderBy,
    order,
    rowsPerPage,
    onRowsPerPageChange,
    selectedContent,
    setLoading,
    filterMenu,
    handleDeleteItems,
    handlePageChange,
    handleSortChange,
    searchBar,
    searchState,
    sourceFilter,
    queryPills,
    SnackAlertHandler,
    title = "Files",
  } = props;
  const { SnackAlert } = SnackAlertHandler;

  const { handleUpdateRowsPerPage } = useMediaTable(
    { onRowsPerPageChange },
    rowsPerPage,
  );

  const { updateIncludeDialog } = useContext(OrganizeContext);
  const openIncludeDialog = action => event => {
    updateIncludeDialog({
      open: true,
      action: action,
      activeType: IncludeDialogTypes.content,
      selected: selectedContent,
    });
  };

  const [scrollVisible, setScrollVisible] = useState(true);
  const [contentList, setContentList] = useState([]);

  useEffect(() => {
    setContentList(content);
  }, [content]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  return (
    <Fragment>
      <Grid container item xs className={classes.root}>
        {loading && (
          <PanelLoadingIndicator
            transparent
            className={classes.loadingIndicator}
          />
        )}

        <SnackAlert />

        <Grid item xs={12}>
          <MediaBrowser
            loaded={true}
            organizeTable
            scrollVisible={scrollVisible}
            searchBar={
              <Grid container justify="space-between">
                <Grid container item xs spacing={3} alignItems="center">
                  <Fragment>
                    <Grid item>
                      <Typography variant="h2">
                        {title} ({total})
                      </Typography>
                    </Grid>

                    {notBlank(selectedContent) && (
                      <Grid item container xs alignItems="center">
                        <Grid item>
                          <SelectedFilesCheckbox
                            isPagedContent
                            total={total}
                            content={content}
                          />
                        </Grid>

                        {!isContentGroupsHome(view) && (
                          <Grid item style={{ paddingLeft: 10 }}>
                            <Tooltip title="Move" placement="bottom">
                              <IconButton onClick={openIncludeDialog("move")}>
                                <FontAwesomeIcon icon={faFolderPlus} />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        )}

                        {!isUngroupedHome(view) && (
                          <Grid item>
                            <Tooltip title="Copy" placement="bottom">
                              <IconButton onClick={openIncludeDialog("copy")}>
                                <FontAwesomeIcon icon={faCopy} />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        )}

                        {!isContentGroupTopLevelView(view) && (
                          <Grid item>
                            <Tooltip title="Remove">
                              <IconButton
                                onClick={() =>
                                  handleDeleteItems(selectedContent)
                                }
                              >
                                <FontAwesomeIcon icon={faCircleMinus} />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Fragment>
                </Grid>
                <Grid item container alignItems="center" xs>
                  {notBlank(searchBar) && (
                    <Grid item xs>
                      {searchBar}
                    </Grid>
                  )}

                  {notBlank(filterMenu) && (
                    <Grid item xs="auto">
                      {filterMenu}
                    </Grid>
                  )}
                </Grid>

                {notBlank(queryPills) && (
                  <Grid item container alignItems="center">
                    {queryPills}
                  </Grid>
                )}
              </Grid>
            }
            mediaTable={
              <ContentGroupFilesTable
                isLoading={loading}
                searchState={searchState}
                sourceFilter={sourceFilter}
                parent={parent}
                files={notBlank(contentList) ? contentList : []}
                selectedContent={selectedContent}
                setLoading={setLoading}
                onItemsDelete={handleDeleteItems}
                order={order}
                orderBy={orderBy}
                onSortChange={handleSortChange}
                onScrollVisible={handleScrollVisible}
              />
            }
          />
        </Grid>
      </Grid>

      <CustomTablePagination
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangeRowsPerPage={event =>
          handleUpdateRowsPerPage(+event.target.value)
        }
        onChangePage={handlePageChange}
      />
    </Fragment>
  );
};

export default ContentGroupFilesBrowser;
